// =============================================================================
// BrandingCard - Identità visiva dell'organizzazione
// =============================================================================
// L'organizzazione sceglie tra due strade: caricare il proprio logo oppure
// usare un'icona standard su un colore. L'avatar risultante compare ovunque
// l'organizzazione sia nominata (switcher, selezione organizzazione, inviti)
// e nella pagina pubblica di prenotazione.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Palette, Save, Trash2 } from 'lucide-react';
import { useUpdateTenantBranding } from '../../api/hooks';
import type { TenantDetail, UpdateTenantBrandingData } from '../../api/hooks';
import {
  OrgAvatar,
  ORG_COLOR_PRESETS,
  ORG_ICON_NAMES,
  resolveOrgIcon,
  hexToRgba,
  type OrgBranding,
} from '../common/OrgAvatar';
import {
  ImageTooLargeError,
  MAX_SOURCE_BYTES,
  resizeImageToDataUrl,
} from '../../utils/imageResize';
import { useReportDirty } from './settingsDirty';
import {
  Button,
  Feedback,
  SectionHeader,
  SettingsCard,
  type FeedbackProps,
} from './_shared';

type BrandMode = 'logo' | 'icon';

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp';

interface BrandingCardProps {
  tenant: TenantDetail;
  /// Ancora per l'indice di navigazione della pagina Organizzazione
  id?: string;
  /// Notifica al tab contenitore le modifiche non salvate (pallino sulla sezione)
  onDirtyChange?: (dirty: boolean) => void;
}

export function BrandingCard({ tenant, id, onDirtyChange }: BrandingCardProps) {
  const { t } = useTranslation('settings');
  const updateBranding = useUpdateTenantBranding();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const baseline = tenant.branding;

  const [mode, setMode] = useState<BrandMode>(baseline.logoUrl ? 'logo' : 'icon');
  const [icon, setIcon] = useState(baseline.icon);
  const [color, setColor] = useState(baseline.color);
  /// undefined = logo invariato, null = da rimuovere, stringa = nuovo data URL
  const [pendingLogo, setPendingLogo] = useState<string | null | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackProps | null>(null);

  // Riallinea il form quando arrivano dati freschi dal server
  useEffect(() => {
    setMode(baseline.logoUrl ? 'logo' : 'icon');
    setIcon(baseline.icon);
    setColor(baseline.color);
    setPendingLogo(undefined);
  }, [baseline.logoUrl, baseline.icon, baseline.color]);

  // Logo mostrato in anteprima: quello appena scelto, altrimenti il salvato
  const previewLogo = pendingLogo !== undefined ? pendingLogo : baseline.logoUrl;
  const previewBranding: OrgBranding = {
    logoUrl: mode === 'logo' ? previewLogo : null,
    icon,
    color,
  };

  const isDirty =
    mode !== (baseline.logoUrl ? 'logo' : 'icon') ||
    icon !== baseline.icon ||
    color.toLowerCase() !== baseline.color.toLowerCase() ||
    (mode === 'logo' && pendingLogo !== undefined);

  useReportDirty('organization-branding', isDirty);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // In modalità logo serve un'immagine: senza, non c'è niente da salvare
  const missingLogo = mode === 'logo' && !previewLogo;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setFeedback(null);
    setIsProcessing(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      setPendingLogo(dataUrl);
      setMode('logo');
    } catch (err) {
      setFeedback({
        type: 'error',
        message:
          err instanceof ImageTooLargeError
            ? t('branding.errors.tooLarge', {
                size: Math.round(MAX_SOURCE_BYTES / (1024 * 1024)),
              })
            : t('branding.errors.invalidImage'),
      });
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    const payload: UpdateTenantBrandingData = { icon, color };

    if (mode === 'logo') {
      // undefined = il logo salvato resta com'è
      if (pendingLogo !== undefined) payload.logo = pendingLogo;
    } else if (baseline.logoUrl || pendingLogo) {
      // Passando all'icona il logo caricato non serve più
      payload.logo = null;
    }

    updateBranding.mutate(payload, {
      onSuccess: () => {
        setPendingLogo(undefined);
        setFeedback({ type: 'success', message: t('branding.saved') });
        setTimeout(() => setFeedback(null), 3000);
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || t('branding.errors.saveFailed');
        setFeedback({ type: 'error', message: typeof msg === 'string' ? msg : msg[0] });
        setTimeout(() => setFeedback(null), 5000);
      },
    });
  };

  return (
    <SettingsCard id={id}>
      <SectionHeader
        icon={Palette}
        title={t('branding.title')}
        description={t('branding.description')}
      />

      {/* Anteprima: come l'organizzazione appare nelle liste */}
      <div className="flex items-center gap-3 p-4 mb-5 rounded-xl border border-gray-200 bg-gray-50/60">
        <OrgAvatar branding={previewBranding} name={tenant.name} size="lg" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{tenant.name}</p>
          <p className="text-xs text-gray-500">{t('branding.previewHint')}</p>
        </div>
      </div>

      {/* Scelta della modalità */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <ModeOption
          selected={mode === 'logo'}
          onSelect={() => setMode('logo')}
          title={t('branding.modeLogo')}
          description={t('branding.modeLogoHint')}
        />
        <ModeOption
          selected={mode === 'icon'}
          onSelect={() => setMode('icon')}
          title={t('branding.modeIcon')}
          description={t('branding.modeIconHint')}
        />
      </div>

      {mode === 'logo' ? (
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            icon={ImagePlus}
            loading={isProcessing}
            onClick={() => fileInputRef.current?.click()}
          >
            {previewLogo ? t('branding.replaceLogo') : t('branding.uploadLogo')}
          </Button>

          {previewLogo && (
            <Button
              variant="ghost"
              icon={Trash2}
              onClick={() => setPendingLogo(null)}
            >
              {t('branding.removeLogo')}
            </Button>
          )}

          <p className="text-xs text-gray-500 w-full">{t('branding.logoFormatsHint')}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Colore */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">
              {t('branding.colorLabel')}
            </span>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex flex-wrap gap-2">
                {ORG_COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-7 h-7 rounded-full transition-transform ${
                      color.toLowerCase() === c.toLowerCase()
                        ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                        : ''
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={c}
                  />
                ))}
              </div>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                aria-label={t('branding.customColor')}
              />
            </div>
          </div>

          {/* Icona */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">
              {t('branding.iconLabel')}
            </span>
            <div className="grid grid-cols-8 gap-2 sm:grid-cols-10">
              {ORG_ICON_NAMES.map((name) => {
                const Icon = resolveOrgIcon(name);
                const selected = icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setIcon(name)}
                    className={`flex items-center justify-center aspect-square rounded-lg border transition-colors ${
                      selected ? 'ring-2' : 'border-gray-200 hover:bg-gray-50 text-gray-500'
                    }`}
                    style={
                      selected
                        ? {
                            borderColor: color,
                            color,
                            backgroundColor: hexToRgba(color, 0.08),
                          }
                        : undefined
                    }
                    aria-label={name}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center gap-4">
        <Button
          onClick={handleSave}
          loading={updateBranding.isPending}
          disabled={!isDirty || missingLogo || isProcessing}
          icon={Save}
        >
          {t('branding.save')}
        </Button>
        {feedback ? (
          <Feedback {...feedback} />
        ) : missingLogo ? (
          <span className="text-sm text-gray-500">{t('branding.uploadRequired')}</span>
        ) : isDirty ? (
          <span className="flex items-center gap-1.5 text-sm text-amber-600">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            {t('organization.unsavedChanges')}
          </span>
        ) : null}
      </div>
    </SettingsCard>
  );
}

// -----------------------------------------------------------------------------
// Scelta logo vs icona
// -----------------------------------------------------------------------------

interface ModeOptionProps {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}

function ModeOption({ selected, onSelect, title, description }: ModeOptionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left px-4 py-3 rounded-xl border transition-colors ${
        selected
          ? 'border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500/30'
          : 'border-gray-200 hover:bg-gray-50'
      }`}
    >
      <span className="block text-sm font-medium text-gray-900">{title}</span>
      <span className="block text-xs text-gray-500 mt-0.5">{description}</span>
    </button>
  );
}
